import styled, { css } from 'styled-components';
import { Link } from 'react-router-dom';
import { SelectButton, SearchPopover } from '../../styles/styles';

// ----------------------------------------------------------------------

export const Container = styled.div`
  width: 100%;
  height: 100%;
`;

export const Header = styled.header<{ $active: boolean }>`
  width: 100%;
  height: ${({theme}) => theme.headers.height};
  box-shadow: 0px 2px 5px 0px rgba(38, 51, 77, 0.03);
  position: fixed;
  top: 0;
  left: 0;
  background-color: #FFFFFF;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0px 20px;
  z-index: 10;
  
  > div:first-child {
    display: flex;
    align-items: center;

    > button:first-child {
      width: 28px;
      height: 28px;
      border-radius: 4px;
      background-color: ${({theme}) => theme.colors.grayscale[100]};

      > svg {
        stroke: #7D8FB3;
      }
    }
  }
    
  ${({theme}) => theme.media.md`
    background-color: #22345E;

    > div:first-child > button:first-child {
      background-color: transparent;

      > svg {
        stroke: #C3CAD9;
      }
    }
  `};

  ${(props) => props.$active && `
    > div:first-child > button:first-child {
      background-color: transparent;
    }
  `};
`;

export const Close = styled.div`
  width: 100%;
  height: 56px;
  padding: 0 16px;
  display: flex;
  justify-content: flex-end;
  align-items: center;

  button {
    width: 24px;
    height: 24px;
    
    svg {
      stroke: ${({theme}) => theme.colors.grayscale[300]};
    }
  }
`;

export const GroupsButton = styled(SelectButton)<{ $active: boolean }>`
  width: 215px;
  margin-left: 60px;
  color: ${({theme}) => theme.colors.grayscale[600]};

  > div:last-child {
    transition: all .2s ease-in-out;
  }

  ${(props) => props.$active && `
    background-color: ${props.theme.colors.grayscale[50]};
    > div:last-child {
      transform: rotate(180deg);
    }
  `};
    
  ${({theme}) => theme.media.md`
    background-color: transparent;
    margin-left: 0;
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    justify-content: center;
    border: none;

    > p {
      width: auto;
      color: #F7F8FA;
    }

    > div:last-child {
      width: 25px;
      background-image: url(/assets/base/chevron-fill.svg);
      margin-left: 0;
    }
  `};
`;

export const AccountButton = styled(SelectButton)<{ $active: boolean }>`
  > div:first-child {
    color: ${({theme}) => theme.colors.grayscale[600]};
    display: flex;
    align-items: center;
    > div {
      margin: 0 3px 0 8px;
    }
    span {
      color: ${({theme}) => theme.colors.grayscale[300]};
    }
  }
    
  > div:last-child {
    transition: all .2s ease-in-out;
  }

  ${(props) => props.$active && `
    background-color: ${props.theme.colors.grayscale[50]};
    > div:last-child {
      transform: rotate(180deg);
    }
  `};
    
  ${({theme}) => theme.media.md`
    width: 24px;
    height: 24px;
    background-image: url(/assets/base/user.svg);
    background-size: 24px 24px;
    background-position: center center;
    background-repeat: no-repeat;
    padding: 0;
    border: none;
    background-color: transparent;
  `};
`;

export const AccountPopover = styled.div`
  > div, > button {
    display: flex;
    align-items: center;
    color: ${({theme}) => theme.colors.grayscale[600]};
  }

  > div {
    padding: 10px 20px;
    > svg {
      margin-right: 8px;
    }
    > p {
      font-weight: 600;
      span {
        font-weight: 400;
      }
    }
  }

  > button {
    > svg {
      margin-right: 10px;
    }
    &:hover {
      color: ${({theme}) => theme.colors.primary[400]};
      > svg {
        stroke: ${({theme}) => theme.colors.primary[400]};
      }
    }
    &:nth-last-child(2), &:nth-last-child(3), &:nth-last-child(4) {
      border-top: none;
    }
  }
`;

export const MobilePopover = styled.div<{ $visible: boolean }>`
  width: 100%;
  display: none;
  height: calc(100% - ${({theme}) => theme.headers.height});
  background-color: #FFFFFF;
  position: fixed;
  top: 60px;
  left: 0;
  z-index: 10;
  padding: 0 16px;

  ${Close} {
    padding: 0;
  }

  ${SearchPopover} {
    > div:first-child {
      width: 100%;
      margin: 0;
    }

    > button {
      width: 100%;
      text-align: center;
      height: 50px;
      background-color: #F7F8FA;
      border-radius: 8px;
      margin-top: 10px;
      font-size: 16px;
    }
  }

  ${AccountPopover} {
    > div:first-child {
      height: 50px;
      padding: 0 50px;
      font-size: 18px;
      
      > svg {
        width: 32px;
        height: 32px;
      }
    }

    > p {
      color: ${({theme}) => theme.colors.grayscale[600]};
      font-size: 14px;
      padding: 0 50px 10px;
    }

    > button {
      width: 100%;
      text-align: center;
      height: 50px;
      background-color: #F7F8FA;
      border-radius: 8px;
      margin-top: 10px;
      font-size: 16px;
      padding: 0 50px;
      position: relative;

      > svg {
        width: 24px;
        height: 24px;
      }
    }
  }

  ${(props) => props.$visible && `
    display: block;
  `};
`;

export const Wrapper = styled.div`
  width: 100%;
  min-height: 100%;
  padding-top: ${({theme}) => theme.headers.height};
  background-color: ${({theme}) => theme.colors.grayscale[50]};
`;

export const Nav = styled.nav<{ $active: boolean }>`
  width: ${({theme}) => theme.nav.active};
  overflow: hidden;
  background-color: ${({theme}) => theme.colors.grayscale[800]};
  height: calc(100% - ${({theme}) => theme.headers.height});
  position: fixed;
  bottom: 0;
  left: 0;
  padding: 20px 10px 0px;
  transition: all .2s ease-in-out;

  ${(props) => props.$active && `
    width: ${props.theme.nav.desktop};
  `};
    
  ${({theme}) => theme.media.lg`
    width: 55px;
    padding: 20px 5px 0px;
  `};

  ${(props) => props.$active && props.theme.media.lg`
    width: 142px;
  `};
    
  ${({theme}) => theme.media.md`
    width: 100%;
    display: block;
    z-index: 10;
    background-color: #FFFFFF;
    padding: 0;
    top: 60px;
    transition: none;
  `};

  ${(props) => props.$active && props.theme.media.md`
    display: none;
  `};
`;

const GnbStyles = css`
  display: block;
  width: 60px;
  border-radius: 20px;
  font-size: 16px;
  letter-spacing: -0.2px;
  transition: all .2s ease-in-out;
  color: ${({theme}) => theme.colors.grayscale[800]};

  > div {
    width: 220px;
    height: 60px;
    padding: 0px 18px;
    display: flex;
    align-items: center;

    > svg, > div {
      margin-right: 18px;
      display: flex;
    }
  }
    
  ${({theme}) => theme.media.lg`
    width: 45px;
    border-radius: 15px;
    font-size: 14px;

    > div {
      width: 132px;
      height: 45px;
      padding: 0px 12px;

      svg {
        width: 20px;
        height: 20px;
      }

      > svg, > div {
        margin-right: 12px;
      }
    }
  `};
`;

export const GnbLink = styled(Link)<{ $active: boolean, $display: boolean }>`
  ${ GnbStyles }
  background-color: ${(props) => props.$display ? props.theme.colors.primary[400] : ''};

  ${(props) => props.$active && props.theme.media.lg`
    width: 132px;
  `};

  ${(props) => props.$active && `
    width: 220px;
    color: ${props.theme.colors.grayscale[100]};
  `};
    
  ${({theme}) => theme.media.md`
    width: 100%;
    background-color: transparent;

    > div {
      width: 100%;
      height: 50px;
      padding: 0px 40px;
    }
  `};
`;

export const GnbButton = styled.button<{ $visible: boolean, $display: boolean, $active: boolean }>`
  ${ GnbStyles }
  height: 60px;
  position: relative;
  background-color: ${(props) => props.$display ? props.theme.colors.primary[400] : ''};

  &:after {
    content: '';
    width: 0;
    height: 24px;
    background-image: url(/assets/base/chevron.svg);
    background-size: 100% auto;
    background-position: center center;
    background-repeat: no-repeat;
    position: absolute;
    top: 50%;
    right: 18px;
    transform: translateY(-50%) ${(props) => props.$visible ? '' : 'rotate(-90deg)'};
    transition: all .2s ease-in-out;
  }

  ${(props) => props.$active && `
    width: 220px;
    color: ${props.theme.colors.grayscale[100]};
    &:after {
      width: 24px;
    }
  `};

  ${(props) => props.$active && props.theme.media.lg`
    width: 132px;
  `};
    
  ${({theme}) => theme.media.lg`
    height: 45px;

    &:after {
      width: 0;
    }
  `};
    
  ${({theme}) => theme.media.md`
    width: 100%;
    background-color: transparent;
    &:after {
      width: 24px;
    }

    > div {
      width: 100%;
      height: 50px;
      padding: 0px 40px;
    }
  `};
`;

export const Lnb = styled.div<{ $visible: boolean, $length: number }>`
  height: ${(props) => props.$visible ? `${40 * props.$length + 20}px` : 0 };
  overflow: hidden;
  transition: all .2s ease-in-out;

  > a {
    display: block;
    height: 40px;
    padding-left: 60px;
    color: ${({theme}) => theme.colors.grayscale[100]};
    line-height: 40px;
    border-radius: 15px;

    &:first-child {
      margin-top: 5px;
    }

    &:last-child {
      margin-bottom: 15px;
    }

    &.active {
      background-color: ${({theme}) => theme.colors.grayscale[50]}1A;
    }
  }
    
  ${({theme}) => theme.media.lg`
    > a {
      padding-left: 44px;
    }
  `};
    
  ${({theme}) => theme.media.md`
    > a {
      color: #6B7A99;
      padding-left: 82px;
    }
  `};
`;

export const Logo = styled.div<{ $active: boolean }>`
  width: 100%;
  height: 80px;
  background-image: url(/assets/logo/logo-white-small.svg);
  background-size: 30px 30px;
  background-position: center center;
  background-repeat: no-repeat;
  position: absolute;
  bottom: 0;
  left: 0;
  transition: all .2s ease-in-out;
    
  ${({theme}) => theme.media.lg`
    background-size: 0 0;
  `};
    
  ${({theme}) => theme.media.md`
    height: 60px;
    background-image: url(/assets/logo/logo.svg);
    background-size: 150px 26px;
  `};

  ${(props) => props.$active && `
    background-image: url(/assets/logo/logo-white.svg);
    background-size: 180px 32px;
  `};
`;

export const Main = styled.main<{ $active: boolean }>`
  width: 100%;
  min-height: 100%;
  padding-left: ${({theme}) => theme.nav.active};
  transition: all .2s ease-in-out;

  ${(props) => props.$active && `
    padding-left: ${props.theme.nav.desktop};
  `};
    
  ${({theme}) => theme.media.lg`
    padding-left: 55px;
  `};

  ${(props) => props.$active && props.theme.media.lg`
    padding-left: 142px;
  `};
    
  ${({theme}) => theme.media.md`
    padding-left: 0;
  `};
`;

export const DevicesList = styled.div<{ $active: boolean }>`
  width: 210px;
  height: calc(100vh - ${({theme}) => theme.headers.height});
  position: fixed;
  bottom: 0;
  padding: 30px 0;
  left: ${({theme}) => theme.nav.active};
  background-color: ${({theme}) => theme.colors.grayscale[700]};
  transition: all .2s ease-in-out;

  ${(props) => props.$active && `
    left: ${props.theme.nav.desktop};
  `};
    
  ${({theme}) => theme.media.xl`
    width: 170px;
  `};
    
  ${({theme}) => theme.media.lg`
    left: 55px;
  `};

  ${(props) => props.$active && props.theme.media.lg`
    left: 142px;
  `};
`;

export const DevicesButton = styled.button<{ $active: boolean }>`
  width: 100%;
  height: 60px;
  padding: 0 20px;
  color: #FFFFFF;
  display: flex;
  align-items: center;

  &:before {
    content: '';
    display: block;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background-color: #FFFFFF;
    margin-right: 10px;
  }

  ${(props) => props.$active && `
    background-color: ${props.theme.colors.primary[400]};
  `};
`;

export const ScrollBar = styled.div`
  ${({theme}) => theme.media.md`
    width: 100%;
    height: calc(100% - 116px);
    overflow: overlay;
  `};

  &::-webkit-scrollbar {
    width: 18px;
    height: 18px;
  }
  &::-webkit-scrollbar-thumb {
    background-color: rgba(99, 115, 129, 0.3);
    border-radius: 10px;
    background-clip: padding-box;
    border: 7px solid transparent;
  },
  &::-webkit-scrollbar-track {
    background-color: transparent;
  }
`;